/* Storefront-wide video behaviour: silent, looping, and playing only while
   on screen.

   Every video tag in the theme already renders with `muted`, but product media
   plays through a player with controls, so a visitor can unmute it — the muted
   flag is held on here. Muting is also what allows browsers to autoplay at all.
   Playback starts when a video scrolls into view and pauses when it leaves, so
   offscreen clips are not decoding in the background. */
(() => {
  const VISIBLE_RATIO = 0.25;

  const silence = (video) => {
    if (!video.muted) video.muted = true;
    if (video.volume !== 0) video.volume = 0;
  };

  const start = (video) => {
    silence(video);
    // Autoplay can still be refused; nothing breaks if it is.
    video.play?.().catch(() => {});
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        target.dataset.fwInView = isIntersecting ? 'true' : 'false';
        if (isIntersecting) {
          start(target);
        } else {
          target.pause?.();
        }
      });
    },
    { threshold: VISIBLE_RATIO }
  );

  const prepare = (video) => {
    if (video.dataset.fwVideo === 'true') return;
    video.dataset.fwVideo = 'true';

    silence(video);
    video.setAttribute('muted', '');
    // Required for inline autoplay on iOS.
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.loop = true;
    video.setAttribute('loop', '');

    ['play', 'playing', 'loadedmetadata', 'volumechange'].forEach((event) =>
      video.addEventListener(event, () => silence(video))
    );

    // These load lazily (preload="metadata"), so a video scrolled into view
    // before it had data would never start; retry once data arrives.
    ['loadeddata', 'canplay'].forEach((event) =>
      video.addEventListener(event, () => {
        if (video.dataset.fwInView === 'true') start(video);
      })
    );
    // Safety net for any source that ignores the loop attribute.
    video.addEventListener('ended', () => {
      video.currentTime = 0;
      video.play?.().catch(() => {});
    });

    // The homepage band's clip plays only when fully in frame; fw-video.js handles it.
    if (!video.closest('.fw-video')) observer.observe(video);
  };

  /* Product-gallery videos ship inside <deferred-media>, which waits for a
     play click before mounting the <video>. Activate them as they scroll into
     view so they autoplay; the mounted video is then muted/looped/played by
     the handlers above. The poster's play badge is hidden in CSS for gallery
     slides only — thumbnails keep theirs. */
  const activateDeferred = new IntersectionObserver(
    (entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (!isIntersecting || target.dataset.fwActivated === 'true') return;
        const play = target.querySelector('[ref="deferredMediaPlayButton"]');
        if (!play) return;
        target.dataset.fwActivated = 'true';
        play.click();
      });
    },
    { threshold: 0.25 }
  );

  // Story-page tile clips play on hover on desktop (fw-video.js); leave them to it there.
  const hoverTiles = window.matchMedia('(hover: hover) and (min-width: 900px)').matches;
  const scan = () => {
    document.querySelectorAll(hoverTiles ? 'video:not(.fw-steps__video)' : 'video').forEach(prepare);
    document
      .querySelectorAll('.product-information deferred-media, media-gallery deferred-media')
      .forEach((el) => {
        if (el.dataset.fwObserved === 'true') return;
        el.dataset.fwObserved = 'true';
        activateDeferred.observe(el);
      });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  // Product media and the cart drawer mount video after load.
  new MutationObserver(scan).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();
